<template>
  <pop-up
    title="Add a card"
    button-text="Add card"
    :spinning="loading"
    class="top-fade-in"
    @add="onSubmit"
    @close="$emit('close')"
  >
    <div
      class="position-relative"
    >
      <validation-observer ref="card">
        <div>
          <b-form-group
            label="Cardholder Name"
            label-for="cardName"
            :class="{'opacity-7': loading}"
          >
            <validation-provider
              #default="{ errors }"
              name="Cardholder Name"
              rules="required"
            >
              <b-form-input
                id="cardName"
                v-model="billing_details.name"
                :state="(errors.length > 0) ? false:null"
                placeholder="Enter name here..."
              />
              <small
                class="text-danger"
              >{{ errors[0] }}</small>
            </validation-provider>

            <div class="card-details mb-2">
              <label for="card-element">Card</label>
              <div
                id="card-element"
                :class="{ 'error': cardError }"
              />
              <span
                class="text-danger small"
              >{{ errorMessage }}</span>
            </div>
            <validation-provider
              #default="{ errors }"
              name="Country"
              rules="required"
            >
              <input-field
                label="Country"
                label-position="top"
                :error="errors.length ? errors[0] : null"
              >
                <b-form-select
                  id="country"
                  v-model="billing_details.address.country"
                  :state="errors.length > 0 ? false:null"
                  :options="getCountryOptions(countryOptions)"
                />
              </input-field>
            </validation-provider>
            <validation-provider
              #default="{ errors }"
              name="Address line 1"
              rules="required"
            >
              <input-field
                label="Address line 1"
                label-position="top"
                :error="errors.length ? errors[0] : null"
              >
                <b-form-input
                  v-model="billing_details.address.line1"
                  :state="errors.length > 0 ? false:null"
                  placeholder="Address line 1"
                />
              </input-field>
            </validation-provider>
            <input-field
              class="mb-2"
              label="Address line 2"
              label-position="top"
            >
              <b-form-input
                v-model="billing_details.address.line2"
                placeholder="Address line 2"
              />
            </input-field>
            <validation-provider
              #default="{ errors }"
              name="Postal Code"
              rules="required"
            >
              <input-field
                label="Postal Code"
                label-position="top"
                :error="errors.length ? errors[0] : null"
              >
                <b-form-input
                  v-model="billing_details.address.postal_code"
                  :state="errors.length > 0 ? false:null"
                  placeholder="Postal code"
                />
              </input-field>
            </validation-provider>
            <validation-provider
              #default="{ errors }"
              name="City"
              rules="required"
            >
              <input-field
                label="City"
                label-position="top"
                :error="errors.length ? errors[0] : null"
              >
                <b-form-input
                  v-model="billing_details.address.city"
                  :state="errors.length > 0 ? false:null"
                  placeholder="City"
                />
              </input-field>
            </validation-provider>
          </b-form-group>
          <div class="stripe d-flex">
            <span>Powered by</span>
            <img
              :src="require('@/assets/svg/stripe-slate.svg')"
              class="stripe_logo"
              alt="stripe logo"
            >
          </div>
        </div>
      </validation-observer>
      <loading-page
        v-if="loading"
        id="loading"
      />
    </div>
  </pop-up>
</template>
<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import PopUp from '@/components/popup/Pop-up.vue'
import countryOptions from '../countries'
import loadStripe from '@/services/loadStripe'

export default {
  name: 'AddCard',
  components: {
    ValidationProvider,
    ValidationObserver,
    PopUp,
  },
  data() {
    return {
      required,
      countryOptions,
      cardName: '',
      cardElement: undefined,
      cardError: false,
      errorMessage: null,
      loading: true,
      billing_details: {
        name: '',
        address: {
          country: null,
          line1: '',
          line2: '',
          postal_code: null,
          city: '',
        },
      },
      stripe: null,
    }
  },
  computed: {
    getStripe() {
      return this.$store.state.stripe ? this.$store.state.stripe.stripe : null
    },
  },
  watch: {
    getStripe() {
      this.loadScript()
    },
  },
  mounted() {
    if (this.getStripe === null) {
      loadStripe()
    } else { this.loadScript() }
  },
  methods: {
    onSubmit() {
      return new Promise((resolve, reject) => {
        this.$refs.card.validate().then(success => {
          if (success) {
            resolve(true)
            this.submitCard()
          } else {
            reject()
          }
        })
      })
    },

    submitCard() {
      this.loading = true
      if (this.updatingCard) {
        this.$store.dispatch('organization/deleteBillingDetails').then(() => {
          this.addCard()
        }).catch(error => {
          this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while updating billing details.')
          this.loading = false
        })
      } else {
        this.addCard()
      }
    },

    addCard() {
      this.$store.dispatch('payments/addPaymentMethod')
        .then(() => { this.confirmCard() })
        .catch(error => {
          this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while adding billing details.')
          this.loading = false
        })
    },
    confirmCard() {
      const { clientSecret } = this.$store.state.payments
      this.$store.state.stripe.stripe.confirmCardSetup(
        clientSecret,
        {
          payment_method: {
            card: this.cardElement,
            billing_details: this.billing_details,
          },
        },
      ).then(result => {
        if (result.error) {
          this.errorToast(result.error.message)
          return
        }
        this.$emit('cardAdded')
      }).finally(() => { this.loading = false })
    },
    getCountryOptions(options) {
      return options.map(o => ({ text: o.text, value: o.code }))
    },
    loadScript() {
      const elements = this.$store.state.stripe.stripe.elements()

      const inputStyle = {
        base: {
          fontSize: '14px',
          fontSmoothing: 'antialiased',
          '::placeholder': {
            color: '#8e8e8e',
          },
        },
      }

      this.cardElement = elements.create('card', { style: inputStyle })
      this.cardElement.mount('#card-element')

      this.cardElement.on('change', event => {
        if (event.error) {
          this.errorMessage = event.error.message
          this.cardError = true
        } else {
          this.cardError = false
          this.errorMessage = ''
        }
      })

      this.loading = false
    },
  },
}
</script>
<style lang="scss" scoped>
.stripe-input {
  padding: 0.438rem 1rem;
  background-color: #ffffff;
  background-clip: padding-box;
  border: 1px solid #d8d6de;
  border-radius: 0.25rem;
}

.card-details {
  margin-top: 24px;
  #card-element {
    background: #ffffff;
    padding: 12px 10px;
    border: 1px solid #E0E5EB;
    border-radius: 0.25rem;
    &.error {
      border-color: #e34850;
    }
  }
}
#loading::v-deep {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 0 !important;
}
.stripe {
  display: flex;
  align-items: center;
  justify-content: right;
  &_logo {
    max-width: 100px;
  }
}
</style>
